import * as mobx from 'mobx';

import { post } from '../Shared/utils/ajax-client';
import { AjaxUtils } from '../Shared/utils/ajax-utils';
import { ICancellablePromise } from '../Shared/utils/cancellable-promise';
import { PayerOnboardingApiConfig } from './payer-onboarding-generated';
import { IPayerOnboardingAppState } from './payer-onboarding-app-state';

export interface IApiAction<TRequest, TResponse = any> {
	readonly url: (model: TRequest) => string;
	request: TRequest;
	response: TResponse;
}

export type PayerOnboardingActionRequest<TActionKey extends keyof PayerOnboardingApiConfigActions> = PayerOnboardingApiConfigActions[TActionKey]['request'];
export type PayerOnboardingActionResponse<TActionKey extends keyof PayerOnboardingApiConfigActions> = PayerOnboardingApiConfigActions[TActionKey]['response'];

export type PayerOnboardingApiConfigActions = typeof PayerOnboardingApiConfig['actions'];

export type PayerOnboardingDataService = {
	[x in keyof PayerOnboardingApiConfigActions]:
		(request: PayerOnboardingActionRequest<x>, appState: IPayerOnboardingAppState) => ICancellablePromise<PayerOnboardingActionResponse<x>>;
};

const options = {
	timeout: 20000,
	newCSRFToken: '',
	SessionCorrelationId: '',
};

function createActionHandler<TKey extends keyof PayerOnboardingApiConfigActions>(actionKey: TKey) {
	const actionConfig = PayerOnboardingApiConfig.actions[actionKey] as any;

	return (request: any, appState: IPayerOnboardingAppState) => {
		let customHeaders = [{ header: 'X-PP-PayerOnboarding-CorrelationId', value: options.SessionCorrelationId }];
		if(appState.identityToken) {
			customHeaders.push({ header: 'X-PP-PayerOnboarding-Identity', value: appState.identityToken });
		}

		request = mobx.isObservable(request) ? mobx.toJS(request) : { ...request };

		return post(actionConfig.url(request), request, {
			timeout: options.timeout,
			baseUrl: AjaxUtils.resolveBaseUrl(PayerOnboardingApiConfig.defaultBaseUrl),
			antiForgeryToken: options.newCSRFToken,
			customHeaders: customHeaders,
		}) as any;
	};
}

export function createPayerOnboardingDataService(correlationId: string): PayerOnboardingDataService {
	options.SessionCorrelationId = correlationId;

	return (Object.keys(PayerOnboardingApiConfig.actions) as Array<keyof PayerOnboardingApiConfigActions>)
		.reduce<PayerOnboardingDataService>((acc: PayerOnboardingDataService, actionKey: keyof PayerOnboardingApiConfigActions) => {
			acc[actionKey] = createActionHandler(actionKey);
			return acc;
		}, {} as PayerOnboardingDataService);
}

export function setNewCRSFoken(token: string) {
	options.newCSRFToken = token;
}
